/**
 * Languages / Inshorts tenants - the single source of truth.
 *
 * Everything the rest of the codebase needs to know about a language lives on
 * one row here: our code, the display name, the tenant name Inshorts uses,
 * whether it is switched on, whether it has a national feed, and the state it
 * falls back to when it does not.
 *
 * It sits in `shared/` because the frontend needs the same facts the backend
 * does - the go-live form has to know which languages have no national feed,
 * and it previously carried its own hardcoded copy of that list, which is
 * exactly the drift this file exists to stop.
 *
 * Adding a language means adding one row. Nothing else should hold a list of
 * codes, tenant names or per-language states.
 *
 * Dependency-free, like everything else in `shared/` - see articleExtractor.js.
 */

/**
 * `tenant` is Inshorts' name for the language, used as the key in the hashtag
 * title map (`tenantVsAliasTitleMap`). Not to be confused with the lowercase
 * tenant *code* in their URLs, which is just `code` below.
 *
 * `nationalFeed: false` means the Inshorts feed for that tenant is built purely
 * from location-indexed news. A post published there with an empty news_state
 * is created successfully but never surfaces in any feed, so it must always go
 * out regionally with a state attached - hence `defaultState`.
 *
 * `defaultState` values are the 2-letter codes from INDIAN_STATES
 * (backend/utils/locationExtractor.js). Telugu is spoken in both Telangana and
 * Andhra Pradesh and defaults to Telangana; Inshorts uses TS for it (its feed
 * set is regional_news_set_te_in_TS), not the ISO code TG.
 */
export const LANGUAGES = [
  { code: 'en', name: 'English', tenant: 'ENGLISH', enabled: true, nationalFeed: true, defaultState: null },
  { code: 'hi', name: 'Hindi (हिंदी)', tenant: 'HINDI', enabled: true, nationalFeed: true, defaultState: null },
  { code: 'ta', name: 'Tamil (தமிழ்)', tenant: 'TAMIL', enabled: true, nationalFeed: false, defaultState: 'TN' },
  { code: 'te', name: 'Telugu (తెలుగు)', tenant: 'TELUGU', enabled: true, nationalFeed: false, defaultState: 'TS' },
  { code: 'kn', name: 'Kannada (ಕನ್ನಡ)', tenant: 'KANNADA', enabled: true, nationalFeed: false, defaultState: 'KA' },
  { code: 'ml', name: 'Malayalam (മലയാളം)', tenant: 'MALAYALAM', enabled: false, nationalFeed: false, defaultState: 'KL' },
  { code: 'mr', name: 'Marathi (मराठी)', tenant: 'MARATHI', enabled: true, nationalFeed: false, defaultState: 'MH' },
  { code: 'bn', name: 'Bengali (বাংলা)', tenant: 'BENGALI', enabled: false, nationalFeed: false, defaultState: 'WB' },
  { code: 'gu', name: 'Gujarati (ગુજરાતી)', tenant: 'GUJARATI', enabled: true, nationalFeed: false, defaultState: 'GJ' },
  { code: 'pa', name: 'Punjabi (ਪੰਜਾਬੀ)', tenant: 'PUNJABI', enabled: false, nationalFeed: false, defaultState: 'PB' },
  { code: 'or', name: 'Odia (ଓଡ଼ିଆ)', tenant: 'ODIA', enabled: false, nationalFeed: false, defaultState: 'OD' },
  { code: 'as', name: 'Assamese (অসমীয়া)', tenant: 'ASSAMESE', enabled: false, nationalFeed: false, defaultState: 'AS' },
];

/**
 * What an unspecified language means everywhere: the news language a record
 * defaults to, and the tenant the hashtag list is read with.
 */
export const DEFAULT_LANGUAGE = 'en';

// Indexed once rather than scanned per lookup - these are read on hot paths
const BY_CODE = new Map(LANGUAGES.map((lang) => [lang.code, lang]));

export const LANGUAGE_CODES = LANGUAGES.map((lang) => lang.code);

export const ENABLED_LANGUAGES = LANGUAGES.filter((lang) => lang.enabled);

export const ENABLED_LANGUAGE_CODES = ENABLED_LANGUAGES.map((lang) => lang.code);

/** Codes allowed to publish to the national ("ALL") feed */
export const NATIONAL_FEED_LANGUAGES = LANGUAGES.filter((lang) => lang.nationalFeed).map((lang) => lang.code);

/** `{ ta: 'TN', te: 'TS', ... }` - only languages that have a fallback state */
export const LANGUAGE_DEFAULT_STATE = Object.fromEntries(
  LANGUAGES.filter((lang) => lang.defaultState).map((lang) => [lang.code, lang.defaultState])
);

/** `{ en: 'ENGLISH', hi: 'HINDI', ... }` - our codes to Inshorts' tenant names */
export const TENANT_NAMES = Object.fromEntries(LANGUAGES.map((lang) => [lang.code, lang.tenant]));

/** The full row for a code, or undefined */
export const getLanguage = (code) => BY_CODE.get(code || DEFAULT_LANGUAGE);

/** Display name for a code, falling back to the code itself for unknown ones */
export const getLanguageName = (code) => BY_CODE.get(code)?.name || code;

/**
 * Inshorts' tenant name for a code.
 * Falls back to the default language's tenant, which is what the hashtag title
 * map is keyed by when nothing more specific exists.
 */
export const getTenantName = (code) =>
  BY_CODE.get(code)?.tenant || BY_CODE.get(DEFAULT_LANGUAGE).tenant;

/** Whether a language is allowed to publish to the national ("ALL") feed */
export const isNationalFeedAllowed = (code) =>
  BY_CODE.get(code || DEFAULT_LANGUAGE)?.nationalFeed === true;

/**
 * The state a language falls back to when none was supplied, or null when the
 * language can publish nationally / has no mapping.
 */
export const getDefaultStateForLanguage = (code) => BY_CODE.get(code)?.defaultState || null;
